import { useAuth } from "@dl/web-core-js/auth";
import { useJobCenter } from "@dl/web-core-js/shell";
import { Badge, Button, Card, Dialog, useConfirm } from "@dl/web-core-js/ui";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useMutation } from "@tanstack/react-query";
import type { JobDTO } from "@dl/web-core-js/api";
import { Play } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  EngineeringBomClient,
  PartLifecycleClient,
  formatNumber,
  refusalMessage,
  RoutingClient,
  SigmRefreshClient,
  type EngineeringBomReport,
  type PartLifecycleReport,
  type RoutingSeedReportDTO,
} from "../../api/manufacturing";
import { PipelineLotClient } from "../../api/pipeline";
import { ProductClient } from "../../api/product";
import { ReplenishmentClient } from "../../api/replenishment";
import { SettingsPage } from "../config/SettingsShell";
import { NexpimView } from "../../views";

/**
 * One thing an admin can set running.
 *
 * `run` returns the background job, which the job center then follows; the
 * page never waits on the work itself. A job quick enough to answer inline —
 * the replenishment run takes a second — returns what it did instead, shown as
 * a toast. `consequences` is what the confirm
 * dialog lists, and it is worth being concrete there: these buttons replace
 * cron entries, and a person who could not see the crontab needs the same
 * knowledge the person who wrote it had.
 */
interface AdminAction {
  key: string;
  title: string;
  description: string;
  consequences: string[];
  run: () => Promise<JobDTO | { done: string }>;
}

/**
 * Admin actions: the jobs that otherwise only run from cron, each behind a
 * button.
 *
 * Exists so a recompute after a settings change — a new rank band, a
 * planning window — does not mean finding a shell, the script and the
 * credentials, or waiting for tonight. Every action runs as a background job
 * and shows up in the job center like an export does, so the page is safe to
 * leave once the job has started.
 */
export function ActionsPage() {
  const { t } = useTranslation();
  const { http, canWrite, hasView } = useAuth();
  const confirm = useConfirm();
  const { setOpen, refresh: refreshJobs } = useJobCenter();

  const products = useMemo(() => new ProductClient(http), [http]);
  const pipeline = useMemo(() => new PipelineLotClient(http), [http]);
  const replenishment = useMemo(() => new ReplenishmentClient(http), [http]);
  const sigm = useMemo(() => new SigmRefreshClient(http), [http]);

  // Order is deliberate: the full recompute first, because it is the one
  // reached for after almost every settings change and the one the others
  // are subsets of.
  const actions: AdminAction[] = [
    {
      key: "kpi",
      title: t("actions.kpi.title", "Recompute KPIs and pipeline"),
      description: t(
        "actions.kpi.description",
        "Rebuilds every product's inventory and sales KPIs from SIGM (quantities, velocities, rankings, safety stock) then recomputes the production pipeline against them. This is the nightly job; run it after changing a setting the pipeline reads.",
      ),
      consequences: [
        t("actions.kpi.consequence.duration", "Runs for several minutes; the job center shows progress."),
        t("actions.kpi.consequence.pipeline", "Every pipeline lot is re-planned and its priority may move."),
        t("actions.kpi.consequence.rankings", "Sales rankings and safety-stock floors are rederived."),
      ],
      run: () => products.computeKpi(),
    },
    {
      key: "pipeline",
      title: t("actions.pipeline.title", "Recompute pipeline only"),
      description: t(
        "actions.pipeline.description",
        "Re-plans the production pipeline against the KPIs already computed. Faster than the full recompute; enough after a change to lot sizes, weights, planning windows or dispatch rules.",
      ),
      consequences: [
        t("actions.kpi.consequence.pipeline", "Every pipeline lot is re-planned and its priority may move."),
      ],
      run: () => pipeline.compute(),
    },
    // The SIGM MFI replacement's modules (docs/sigm-mfi-replacement/dark-launch.md): hidden with them.
    ...(!hasView(NexpimView.MANUFACTURING_PARTS) ? [] : [{
      key: "sigm",
      title: t("actions.sigm.title", "Refresh from SIGM"),
      description: t(
        "actions.sigm.description",
        "Brings every SIGM module up to date, in order: parts, bills of materials, routings, stock, purchasing, production (SIGM's runs on the shop floor) and the service desk. Run it after the SIGM copy is restored, while SIGM is still in use.",
      ),
      consequences: [
        t("actions.sigm.consequence.duration", "Runs for several minutes; the job center shows each step."),
        t("actions.sigm.consequence.parts", "Part fields are overwritten with SIGM's values."),
        t("actions.sigm.consequence.kept", "Bills of materials and routings already in nexpim are left alone; SIGM orders closed or cancelled in nexpim stay that way."),
        t("actions.sigm.consequence.stop", "If one step fails, the steps after it do not run; running it again carries on."),
      ],
      run: () => sigm.run(),
    }]),
    // Replenishment belongs to the hidden SIGM MFI replacement (docs/sigm-mfi-replacement/dark-launch.md).
    ...(!hasView(NexpimView.PURCHASING) ? [] : [{
      key: "replenishment",
      title: t("actions.replenishment.title", "Run replenishment"),
      description: t(
        "actions.replenishment.description",
        "Plans every purchased material from current stock, open orders and usage: what the Replenishment screen shows. This is the nightly job, run after the KPI recompute; run it after changing safety days, lead times or truck groups.",
      ),
      consequences: [
        t("actions.replenishment.consequence.duration", "Takes about a second."),
        t("actions.replenishment.consequence.suggestions", "Suggestions are replaced; quantities typed on the Replenishment screen but not drafted are lost."),
        t("actions.replenishment.consequence.drafts", "When automatic drafts are on, untouched nightly drafts are prepared again."),
      ],
      run: async () => {
        const result = await replenishment.run();
        return {
          done: t("actions.replenishment.done", "{{count}} materials planned, {{orders}} with something to order.", {
            count: result.material_count,
            orders: result.order_count,
          }),
        };
      },
    }]),
  ];

  const start = useMutation({
    mutationFn: (action: AdminAction) => action.run(),
    onSuccess: (outcome) => {
      if ("done" in outcome) {
        toast.success(outcome.done);
        return;
      }
      refreshJobs();
      setOpen(true);
      toast.success(outcome.label, { description: t("job.started", "Started") });
    },
    onError: (failure) =>
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const launch = async (action: AdminAction) => {
    const confirmed = await confirm({
      title: action.title,
      message: t("actions.confirm.message", "Start this job now?"),
      consequences: action.consequences,
      confirmLabel: t("actions.run", "Run"),
      variant: "primary",
    });
    if (confirmed) start.mutate(action);
  };

  return (
    <SettingsPage
      title={t("navigation.item.admin-actions", "Actions")}
      subtitle={t("actions.subtitle", "Jobs that otherwise run on a schedule, started by hand.")}
      view={NexpimView.ADMIN_ACTIONS}
    >
      {actions.map((action) => (
        <ActionRow
          key={action.key}
          title={action.title}
          description={action.description}
          control={
            canWrite ? (
              <Button
                variant="primary"
                icon={<Play className="size-4" />}
                disabled={start.isPending}
                loading={start.isPending && start.variables?.key === action.key}
                onClick={() => void launch(action)}
              >
                {t("actions.run", "Run")}
              </Button>
            ) : null
          }
        />
      ))}
      {/* The routing seed belongs to the hidden SIGM MFI replacement, with the plant structure. */}
      {hasView(NexpimView.CONFIG_PLANT) && <RoutingSeedRow canWrite={canWrite} />}
      {/* Engineering BOMs belong to the hidden SIGM MFI replacement too, with the parts. */}
      {hasView(NexpimView.MANUFACTURING_PARTS) && <EngineeringBomRow canWrite={canWrite} />}
      {hasView(NexpimView.MANUFACTURING_PARTS) && <PartLifecycleRow canWrite={canWrite} />}
    </SettingsPage>
  );
}

/**
 * Writes a draft routing into every floor-tracked part the plant makes (on a
 * lot in the last 24 months, or an open one) whose active revision has none,
 * read from the parts' machine lists and edge patterns; a part with no active
 * revision gets a routing-only one. Preview runs it dry and shows what it
 * would write; Run writes it. It never touches a revision that has a routing,
 * so running it again only fills what is still empty. The routings written
 * are drafts, to review and approve under Manufacturing › Routings.
 */
function RoutingSeedRow({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const routings = useMemo(() => new RoutingClient(http), [http]);
  const [report, setReport] = useState<RoutingSeedReportDTO | null>(null);

  const seed = useMutation({
    mutationFn: (dryRun: boolean) => routings.seed(dryRun),
    onSuccess: (result) => {
      if (result.dry_run) {
        setReport(result);
        return;
      }
      toast.success(
        t("actions.routing.done", "{{revisions}} revisions got a routing ({{steps}} steps).", {
          revisions: result.revisions_seeded,
          steps: result.steps_written,
        }),
      );
    },
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const run = async () => {
    const confirmed = await confirm({
      title: t("actions.routing.title", "Seed routings from SIGM"),
      message: t("actions.confirm.message", "Start this job now?"),
      consequences: [
        t("actions.routing.consequence.scope-made", "Only floor-tracked parts made in the last 24 months (or on an open lot) without a routing are written; a part with no active revision gets a routing-only one."),
        t("actions.routing.consequence.draft", "The routings are drafts: a lot is released only once they are approved under Manufacturing › Routings."),
        t("actions.routing.consequence.derived", "Every step written is marked derived until someone edits it."),
        t("actions.routing.consequence.again", "Running it again fills only what is still empty."),
      ],
      confirmLabel: t("actions.run", "Run"),
      variant: "primary",
    });
    if (confirmed) seed.mutate(false);
  };

  const unknown = report ? Object.entries(report.unknown_tokens).sort((a, b) => b[1] - a[1]) : [];

  return (
    <>
      <ActionRow
        title={t("actions.routing.title", "Seed routings from SIGM")}
        description={t(
          "actions.routing.description-made",
          "Writes a draft routing (cut, edge, machining on the machines named, paint, inspection) into every floor-tracked part made in the last 24 months that has none, from the part's edge pattern and machine list. Review and approve them under Manufacturing › Routings.",
        )}
        control={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="secondary" disabled={seed.isPending} loading={seed.isPending && seed.variables === true} onClick={() => seed.mutate(true)}>
                {t("actions.preview", "Preview")}
              </Button>
              <Button
                variant="primary"
                icon={<Play className="size-4" />}
                disabled={seed.isPending}
                loading={seed.isPending && seed.variables === false}
                onClick={() => void run()}
              >
                {t("actions.run", "Run")}
              </Button>
            </div>
          ) : null
        }
      />
      {report && (
        <Dialog open size="lg" onClose={() => setReport(null)} title={t("actions.routing.preview-title", "What the seeding would write")}>
          <div className="flex flex-col gap-3 text-[13px]">
            <ul className="grid gap-1 sm:grid-cols-2">
              <li>{t("actions.routing.considered-made", "Floor-tracked parts made recently: {{n}}", { n: report.parts_considered })}</li>
              <li>{t("actions.routing.revisions", "Revisions to seed: {{n}}", { n: report.revisions_seeded })}</li>
              <li>{t("actions.routing.steps", "Steps: {{n}}", { n: report.steps_written })}</li>
              <li>{t("actions.routing.skipped", "Already with a routing: {{n}}", { n: report.skipped_with_routing })}</li>
              <li className="sm:col-span-2">
                {t("actions.routing.no-revision-made", "Of which a routing-only revision is made (no active revision yet): {{n}}", { n: report.parts_without_revision })}
              </li>
            </ul>
            {unknown.length > 0 && (
              <div>
                <h3 className="mb-1 text-[12px] font-semibold text-ink">{t("actions.routing.unknown", "Machine-list words that name no machine")}</h3>
                <p className="text-[12px] text-ink-muted">
                  {unknown.map(([token, count]) => `${token} (${String(count)})`).join(", ")}
                </p>
              </div>
            )}
            {report.sample.length > 0 && (
              <div>
                <h3 className="mb-1 text-[12px] font-semibold text-ink">{t("actions.routing.sample", "Sample")}</h3>
                <ul className="flex flex-col gap-0.5 font-mono text-[11.5px] text-ink-muted">
                  {report.sample.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
}

/**
 * Builds the engineering BOMs from SIGM's: every BOM per one unit, cut parts
 * consuming virtual panels by area, edgeband by length, and each product's
 * sheets per unit compared, old wood BOM against new.
 *
 * The endpoint answers synchronously, with the report: about 20 s for a dry
 * run (never refreshed from SIGM), about four minutes for the real one. The
 * page waits on it, so the report shows below once it is done.
 */
function EngineeringBomRow({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new EngineeringBomClient(http), [http]);
  const [report, setReport] = useState<EngineeringBomReport | null>(null);

  const build = useMutation({
    mutationFn: (dryRun: boolean) => client.build(dryRun, !dryRun),
    onSuccess: (result) => {
      setReport(result);
      toast.success(
        result.dry_run
          ? t("actions.engineering.dry-done", "Dry run done in {{s}} s: nothing was written.", { s: Math.round(result.duration_ms / 1000) })
          : t("actions.engineering.done", "Engineering BOMs built in {{s}} s.", { s: Math.round(result.duration_ms / 1000) }),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Failed"))),
  });

  const run = async () => {
    const confirmed = await confirm({
      title: t("actions.engineering.confirm.title", "Build the engineering BOMs from SIGM?"),
      message: t("actions.engineering.confirm.message", "SIGM's BOMs are refreshed first, then the engineering BOMs are written."),
      consequences: [
        t("actions.engineering.consequence.duration", "Takes about four minutes; keep this page open until the report shows."),
        t("actions.engineering.consequence.rewrites", "Revision 1 lines imported from SIGM and every generated line are rewritten. Lines people added stay."),
        t("actions.engineering.consequence.panels", "Virtual panels are created or updated; yields and default real panels already set are kept."),
        t("actions.engineering.consequence.again", "Rerunnable: it follows SIGM until the last sync."),
      ],
      confirmLabel: t("actions.engineering.build", "Build from SIGM"),
      variant: "primary",
    });
    if (confirmed) build.mutate(false);
  };

  return (
    <>
      <ActionRow
        title={t("actions.engineering.title", "Engineering BOMs")}
        description={t(
          "actions.engineering.description",
          "Rewrites every BOM for one unit: cut parts take their area of a virtual panel and their edgeband by length, and each product's sheets per unit are compared with its old wood BOM. Start with a dry run: it writes nothing and shows the deviations to check with the plant.",
        )}
        control={
          canWrite ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={build.isPending}
                loading={build.isPending && build.variables === true}
                onClick={() => build.mutate(true)}
              >
                {t("actions.engineering.dry-run", "Dry run")}
              </Button>
              <Button
                variant="primary"
                icon={<Play className="size-4" />}
                disabled={build.isPending}
                loading={build.isPending && build.variables === false}
                onClick={() => void run()}
              >
                {t("actions.engineering.build", "Build from SIGM")}
              </Button>
            </div>
          ) : null
        }
      />
      {build.isPending && (
        <p className="-mt-2 px-1 text-[12px] text-ink-muted">
          {build.variables
            ? t("actions.engineering.running-dry", "Dry run in progress, about 20 seconds.")
            : t("actions.engineering.running", "Refreshing from SIGM and building, about four minutes.")}
        </p>
      )}
      {report && <EngineeringBomReportCard report={report} onClose={() => setReport(null)} />}
    </>
  );
}

type Deviation = EngineeringBomReport["deviations"][number];

/** The report under the action: counts, the comparison, deviations, problems. */
function EngineeringBomReportCard({ report, onClose }: { report: EngineeringBomReport; onClose: () => void }) {
  const { t } = useTranslation();
  const counts = Object.entries(report.counts);
  const problems = Object.entries(report.problem_counts).sort((a, b) => b[1] - a[1]);
  const share = report.products_compared > 0 ? Math.round((report.products_within_25_percent / report.products_compared) * 100) : null;

  const columns = useMemo(() => {
    const c = defineColumns<Deviation>();
    return [
      c.text("product_part_number", (row) => row.product_part_number, { headerKey: "column.engineering.product", priority: 1 }),
      c.text("virtual_panel_number", (row) => row.virtual_panel_number, {
        headerKey: "column.engineering.virtual-panel",
        filter: "select",
        priority: 2,
      }),
      c.number("old_sheets", (row) => (row.old_sheets === null ? null : Number(row.old_sheets)), {
        headerKey: "column.engineering.old-sheets",
        priority: 3,
      }),
      c.number("new_sheets", (row) => (row.new_sheets === null ? null : Number(row.new_sheets)), {
        headerKey: "column.engineering.new-sheets",
        priority: 3,
      }),
      c.custom(
        "ratio",
        (row) => (row.ratio === null ? "" : formatNumber(row.ratio)),
        (row) =>
          row.ratio === null ? (
            <span className="text-ink-faint">{t("actions.engineering.no-ratio", "no old figure")}</span>
          ) : (
            <span className={Math.abs(Number(row.ratio) - 1) > 0.25 ? "font-medium text-risk tabular-nums" : "tabular-nums"}>
              {formatNumber(Math.round(Number(row.ratio) * 100) / 100)}
            </span>
          ),
        { headerKey: "column.engineering.ratio", priority: 2 },
      ),
    ];
  }, [t]);

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[14px] font-semibold text-ink">
          {report.dry_run
            ? t("actions.engineering.report.dry", "Engineering BOMs: dry run, nothing written")
            : t("actions.engineering.report.real", "Engineering BOMs: built")}
        </h2>
        <Badge variant={report.dry_run ? "info" : "success"} size="sm">
          {t("actions.engineering.duration", "{{s}} s", { s: Math.round(report.duration_ms / 1000) })}
        </Badge>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      </div>

      <div className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
        <span>
          {t("actions.engineering.virtual-panels", "Virtual panels: {{n}}", { n: report.virtual_panels.length })}
        </span>
        <span>
          {t("actions.engineering.within", "Products within 25 %: {{within}} of {{compared}}", {
            within: report.products_within_25_percent,
            compared: report.products_compared,
          })}
          {share !== null && <span className="ml-1 text-ink-faint">({share} %)</span>}
        </span>
        {counts.map(([name, value]) => (
          <span key={name}>
            <span className="text-ink-muted">{name.replace(/_/g, " ")}</span>: <span className="tabular-nums">{formatNumber(value)}</span>
          </span>
        ))}
      </div>

      {report.deviations.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="text-[12px] font-semibold text-ink">
            {t("actions.engineering.deviations", "Largest deviations, sheets per unit (old wood BOM against new)")}
          </h3>
          <div className="flex h-80 flex-col rounded-md border border-line">
            <DataTable<Deviation>
              data={report.deviations}
              columns={columns}
              getRowId={(row) => `${row.product_part_number}/${row.virtual_panel_number}`}
              printTitle={t("actions.engineering.deviations", "Largest deviations, sheets per unit (old wood BOM against new)")}
            />
          </div>
        </div>
      )}

      {problems.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="text-[12px] font-semibold text-ink">{t("actions.engineering.problems", "Problems")}</h3>
          <p className="text-[12px] text-ink-muted">
            {problems.map(([kind, count]) => `${kind.replace(/_/g, " ")} (${formatNumber(count)})`).join(", ")}
          </p>
          <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto font-mono text-[11.5px] text-ink-muted">
            {report.problems.slice(0, 100).map((line, index) => (
              <li key={`${String(index)}-${line}`}>{line}</li>
            ))}
          </ul>
          {report.problems.length > 100 && (
            <p className="text-[11px] text-ink-faint">
              {t("actions.engineering.problems-more", "First 100 of {{n}} listed.", { n: report.problems.length })}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Part status from product status: a material is active while a product that is
 * not discontinued uses it, a finished part follows its product, general parts
 * are left alone. The dry run lists what would change; the nightly run waits
 * for its switch in the production settings.
 */
function PartLifecycleRow({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const client = useMemo(() => new PartLifecycleClient(http), [http]);
  const [report, setReport] = useState<PartLifecycleReport | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => client.run(dryRun),
    onSuccess: (result) => {
      setReport(result);
      toast.success(
        result.dry_run
          ? t("actions.lifecycle.dry-done", "Dry run done: nothing was changed.")
          : t("actions.lifecycle.done", "Part statuses updated from product status."),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Failed"))),
  });

  const apply = async () => {
    const confirmed = await confirm({
      title: t("actions.lifecycle.confirm.title", "Update part status from product status?"),
      message: t(
        "actions.lifecycle.confirm.message",
        "Every material part no product that is not discontinued uses becomes inactive, and comes back the same way.",
      ),
      consequences: [
        t("actions.lifecycle.consequence.log", "Every change is logged with its reason."),
        t("actions.lifecycle.consequence.usable", "Inactive parts stay usable everywhere; the service desk can still order them."),
        t("actions.lifecycle.consequence.general", "General parts are never touched."),
      ],
      confirmLabel: t("actions.lifecycle.apply", "Update statuses"),
      variant: "primary",
    });
    if (confirmed) run.mutate(false);
  };

  const columns = useMemo(() => {
    const c = defineColumns<PartLifecycleReport["changes"][number]>();
    return [
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.text("part_group_code", (row) => row.part_group_code ?? "", { headerKey: "column.part.group", filter: "select", priority: 2 }),
      c.text("new_status", (row) => row.new_status, { header: t("actions.lifecycle.becomes", "Becomes"), filter: "select", priority: 1 }),
      c.text("has_stock", (row) => (row.has_stock ? t("actions.lifecycle.stock", "Has stock") : ""), {
        header: t("actions.lifecycle.stock", "Has stock"),
        filter: "select",
        priority: 2,
      }),
      c.text("reason", (row) => row.reason, { header: t("actions.lifecycle.reason", "Why"), priority: 3 }),
    ];
  }, [t]);

  return (
    <>
      <ActionRow
        title={t("actions.lifecycle.title", "Part lifecycle")}
        description={t(
          "actions.lifecycle.description",
          "Sets each material part active while a product that is not discontinued uses it, and each finished part from its product. Start with a dry run; the nightly run waits for its switch in the production settings.",
        )}
        control={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="secondary" disabled={run.isPending} loading={run.isPending && run.variables === true} onClick={() => run.mutate(true)}>
                {t("actions.engineering.dry-run", "Dry run")}
              </Button>
              <Button
                variant="primary"
                icon={<Play className="size-4" />}
                disabled={run.isPending}
                loading={run.isPending && run.variables === false}
                onClick={() => void apply()}
              >
                {t("actions.lifecycle.apply", "Update statuses")}
              </Button>
            </div>
          ) : null
        }
      />
      {report && (
        <Card className="flex flex-col gap-4 p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[14px] font-semibold text-ink">
              {report.dry_run
                ? t("actions.lifecycle.report.dry", "Part lifecycle: dry run, nothing changed")
                : t("actions.lifecycle.report.real", "Part lifecycle: statuses updated")}
            </h2>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setReport(null)}>
              {t("button.close", "Close")}
            </Button>
          </div>
          <div className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(report.counts).map(([name, value]) => (
              <span key={name}>
                <span className="text-ink-muted">{name.replace(/_/g, " ")}</span>: <span className="tabular-nums">{formatNumber(value)}</span>
              </span>
            ))}
          </div>
          {report.changes.length > 0 && (
            <div className="flex h-96 flex-col rounded-md border border-line">
              <DataTable<PartLifecycleReport["changes"][number]>
                data={report.changes}
                columns={columns}
                getRowId={(row) => String(row.part_id)}
                printTitle={t("actions.lifecycle.title", "Part lifecycle")}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/** Title and explanation on the left, the one control on the right. */
function ActionRow({
  title,
  description,
  control,
}: {
  title: string;
  description: string;
  control: ReactNode;
}) {
  return (
    <Card className="flex items-start justify-between gap-6 p-5">
      <div className="min-w-0">
        <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
        <p className="mt-1 max-w-prose text-[12px] leading-relaxed text-ink-muted">{description}</p>
      </div>
      <div className="shrink-0">{control}</div>
    </Card>
  );
}
